import React, { useState } from 'react';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIcon,
  Palette as VariantIcon,
} from '@mui/icons-material';
import Modal from '../Modal';
import VariantCombinationPreview from '../VariantCombinationPreview';
import ToggleButton from '../ToggleButton';
import './VariantDesigner.css';

const VariantDesigner = ({ variants, onVariantsChange, basePrice }) => {
  const [showVariantModal, setShowVariantModal] = useState(false);
  const [editingVariant, setEditingVariant] = useState(null);

  // État pour le formulaire de déclinaison
  const [variantForm, setVariantForm] = useState({
    name: '',
    influencesPrice: false,
    options: [],
  });

  // État pour ajouter une option
  const [newOption, setNewOption] = useState('');

  const openVariantModal = (variant = null) => {
    if (variant) {
      setEditingVariant(variant.id);
      setVariantForm({
        name: variant.name,
        influencesPrice: variant.influencesPrice || false,
        options: [...variant.options],
      });
    } else {
      setEditingVariant(null);
      setVariantForm({
        name: '',
        influencesPrice: false,
        options: [],
      });
    }
    setNewOption('');
    setShowVariantModal(true);
  };

  const closeVariantModal = () => {
    setShowVariantModal(false);
    setEditingVariant(null);
    setVariantForm({ name: '', influencesPrice: false, options: [] });
    setNewOption('');
  };

  const handleVariantNameChange = (e) => {
    setVariantForm({
      ...variantForm,
      name: e.target.value,
    });
  };

  const handleInfluencesPriceChange = (influencesPrice) => {
    // Mettre à jour les options existantes en fonction du nouveau statut
    const updatedOptions = variantForm.options.map((option) => ({
      ...option,
      priceModifier: influencesPrice ? (option.priceModifier ?? 0) : null,
    }));

    setVariantForm({
      ...variantForm,
      influencesPrice,
      options: updatedOptions,
    });
  };

  const handleNewOptionChange = (e) => {
    setNewOption(e.target.value);
  };

  const addOption = () => {
    if (newOption.trim() && !variantForm.options.some((opt) => opt.name === newOption.trim())) {
      const newOptionObj = {
        name: newOption.trim(),
        priceModifier: variantForm.influencesPrice ? 0 : null,
      };
      setVariantForm({
        ...variantForm,
        options: [...variantForm.options, newOptionObj],
      });
      setNewOption('');
    }
  };

  const removeOption = (optionIndex) => {
    setVariantForm({
      ...variantForm,
      options: variantForm.options.filter((opt, index) => index !== optionIndex),
    });
  };

  const updateOptionPrice = (optionIndex, price) => {
    setVariantForm({
      ...variantForm,
      options: variantForm.options.map((opt, index) =>
        index === optionIndex ? { ...opt, priceModifier: price === '' ? 0 : parseFloat(price) || 0 } : opt
      ),
    });
  };

  const saveVariant = () => {
    // Validation
    if (!variantForm.name.trim()) {
      alert('Le nom de la déclinaison est requis');
      return;
    }

    if (variantForm.options.length === 0) {
      alert('Au moins une option est requise');
      return;
    }

    // Validation des prix si la déclinaison influence le prix
    if (variantForm.influencesPrice) {
      const hasInvalidPrice = variantForm.options.some(
        (option) => option.priceModifier === null || isNaN(option.priceModifier)
      );

      if (hasInvalidPrice) {
        alert('Tous les modificateurs de prix doivent être des nombres valides');
        return;
      }
    }

    const newVariant = {
      name: variantForm.name.trim(),
      influencesPrice: variantForm.influencesPrice,
      options: variantForm.options,
    };

    // Ajouter l'ID seulement si on modifie un variant existant
    if (editingVariant) {
      newVariant.id = editingVariant;
    }

    let updatedVariants;
    if (editingVariant) {
      // Modifier une déclinaison existante
      updatedVariants = variants.map((v) => (v.id === editingVariant ? newVariant : v));
    } else {
      // Ajouter une nouvelle déclinaison
      updatedVariants = [...variants, newVariant];
    }

    onVariantsChange(updatedVariants);
    closeVariantModal();
  };

  const deleteVariant = (variantId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette déclinaison ?')) {
      const updatedVariants = variants.filter((v) => v.id !== variantId);
      onVariantsChange(updatedVariants);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addOption();
    }
  };

  return (
    <div className="variant-designer">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium text-gray-700 flex items-center">
          <VariantIcon className="mr-2 text-blue-500" />
          Déclinaisons du produit
        </h3>
        <button
          type="button"
          onClick={() => openVariantModal()}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          <AddIcon className="mr-2" />
          Ajouter une déclinaison
        </button>
      </div>

      {variants.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <VariantIcon className="mx-auto text-4xl mb-2 text-gray-300" />
          <p>Aucune déclinaison définie</p>
          <p className="text-sm">Ajoutez des déclinaisons comme "Couleur", "Taille", etc.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {variants.map((variant) => (
            <div key={variant.id} className="variant-item border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <DragIcon className="text-gray-400 mr-2" />
                    <h4 className="font-medium text-gray-800">{variant.name}</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {variant.options.map((option) => (
                      <span
                        key={option.id}
                        className="inline-block bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                      >
                        {option.name}
                        {variant.influencesPrice && option.priceModifier !== null && (
                          <span className="ml-1 text-xs font-semibold text-blue-600">
                            {option.priceModifier >= 0 ? '+' : ''}
                            {option.priceModifier}€
                          </span>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    type="button"
                    onClick={() => openVariantModal(variant)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                    title="Modifier"
                  >
                    <EditIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteVariant(variant.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                    title="Supprimer"
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Aperçu des combinaisons si des variants existent */}
      {variants.length > 0 && <VariantCombinationPreview variants={variants} basePrice={basePrice} />}

      {/* Modal pour créer/modifier une déclinaison */}
      {showVariantModal && (
        <Modal title={editingVariant ? 'Modifier la déclinaison' : 'Ajouter une déclinaison'} onClose={closeVariantModal}>
          <div className="space-y-6">
            <div>
              <label htmlFor="variantName" className="block text-sm font-medium text-gray-700 mb-2">
                Nom de la déclinaison
              </label>
              <input
                type="text"
                id="variantName"
                value={variantForm.name}
                onChange={handleVariantNameChange}
                placeholder="Ex: Couleur, Taille, Modèle..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <div className="flex items-center space-x-3">
                <span className="text-sm font-medium text-gray-700">
                  Cette déclinaison influence le prix du produit
                </span>
                <ToggleButton
                  value={variantForm.influencesPrice}
                  onChange={handleInfluencesPriceChange}
                  onLabel="Oui"
                  offLabel="Non"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Si activé, vous pourrez définir un modificateur de prix pour chaque option
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Options de la déclinaison</label>

              {/* Liste des options existantes */}
              {variantForm.options.length > 0 && (
                <div className="mb-4 space-y-2">
                  {variantForm.options.map((option, index) => (
                    <div key={option.id || index} className="bg-gray-50 px-3 py-2 rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-gray-700 font-medium">{option.name}</span>
                        <button
                          type="button"
                          onClick={() => removeOption(index)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Supprimer cette option"
                        >
                          <DeleteIcon fontSize="small" />
                        </button>
                      </div>
                      {variantForm.influencesPrice && (
                        <div className="flex items-center space-x-2">
                          <label className="text-xs text-gray-600">Modificateur de prix:</label>
                          <div className="flex items-center">
                            <input
                              type="number"
                              step="0.01"
                              value={option.priceModifier || 0}
                              onChange={(e) => updateOptionPrice(index, e.target.value)}
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0.00"
                            />
                            <span className="ml-1 text-xs text-gray-500">€</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Ajouter une nouvelle option */}
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newOption}
                  onChange={handleNewOptionChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Ex: Rouge, Bleu, Grande..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={addOption}
                  disabled={!newOption.trim()}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  <AddIcon fontSize="small" />
                </button>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                Appuyez sur Entrée ou cliquez sur + pour ajouter une option
              </p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={closeVariantModal}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={saveVariant}
              disabled={!variantForm.name.trim() || variantForm.options.length === 0}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
            >
              {editingVariant ? 'Modifier' : 'Ajouter'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default VariantDesigner;
